import { PickTaskFilters } from './PickTaskFilterBar';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';

/**
 * Pick task columns, grouped by what each column describes rather than by
 * importance. Grouping is what makes the picker's sidebar appear
 * (`ColumnSelection` hides it for a single group).
 *
 * Group order is column order: both `PickTaskTable` and the export payload walk
 * the groups in sequence, so moving a column between groups moves it on screen
 * and in the spreadsheet too.
 */
export const PICK_TASK_COLUMN_ITEMS: ColumnSelectionItem[] = [
  {
    title: 'Pick Task',
    value: [
      { field: 'id', title: 'ID', value: true },
      { field: 'status', title: 'Status', value: true },
      { field: 'lines', title: 'Lines', value: true },
      { field: 'progress', title: 'Shipped / Req', value: true },
      { field: 'sendBy', title: 'Send By', value: true },
      { field: 'pickTaskDays', title: 'Pick Task Days', value: true },
    ],
  },
  {
    title: 'Locations',
    value: [
      { field: 'from', title: 'From Location', value: true },
      { field: 'fromLocationType', title: 'From Location Type', value: true },
      { field: 'to', title: 'To Location', value: true },
      { field: 'toLocationType', title: 'To Location Type', value: true },
    ],
  },
  {
    title: 'Allocation',
    value: [{ field: 'allocatedTo', title: 'Allocated To', value: true }],
  },
  {
    title: 'Audit',
    value: [
      { field: 'createdAt', title: 'Created', value: true },
      { field: 'createdBy', title: 'Created By', value: true },
      { field: 'completedAt', title: 'Completed At', value: true },
      { field: 'cancelledBy', title: 'Cancelled By', value: true },
      { field: 'cancelledAt', title: 'Cancelled At', value: true },
    ],
  },
];

// Sort keys for computed columns (others sort by the field directly).
export const SORT_KEYS: Record<string, string> = {
  allocatedTo: 'allocatedToUserName',
  from: 'fromLocationName',
  to: 'toLocationName',
  createdBy: 'createdByName',
  cancelledBy: 'cancelledByName',
  fromLocationType: 'fromLocationTypeName',
  toLocationType: 'toLocationTypeName',
};

export const EMPTY_FILTERS: PickTaskFilters = {
  statuses: [],
  multiSearch: '',
  advancedFilters: [],
};

// Client-side advanced-filter matcher (the dataset is fetched as an array).
export const matchAdvanced = (val: unknown, operator: string, target: unknown): boolean => {
  const s = (x: unknown) => String(x ?? '').toLowerCase();
  const n = (x: unknown) => Number(x);
  switch (operator) {
    case 'contains':
      return s(val).includes(s(target));
    case 'equals':
      return s(val) === s(target);
    case 'startsWith':
      return s(val).startsWith(s(target));
    case 'endsWith':
      return s(val).endsWith(s(target));
    case 'isEmpty':
      return s(val) === '';
    case '>': {
      const a = n(val);
      const b = n(target);
      return Number.isFinite(a) && Number.isFinite(b) && a > b;
    }
    case '<': {
      const a = n(val);
      const b = n(target);
      return Number.isFinite(a) && Number.isFinite(b) && a < b;
    }
    default:
      return s(val) === s(target);
  }
};
